import isEmpty from 'lodash.isempty'
import { isOfferedSlotStart, periodsUsableForCart } from '@/modules/helpers/deliveryPeriodSlots'

// tasks#170: calculation-handler runs (of every mounted cart watcher) whose "delivery time changed" decision is still
// pending, e.g. while awaiting the period refresh; a "moved" notice is told only when none is
let pendingTimeDecisions = 0

export default {
    created() {
        // tasks#170: this watcher tells a "moved" delivery time (see the calculation handler)
        this.$cart.registerMovedNoticeHandler(1)
    },
    beforeDestroy() {
        this.$cart.registerMovedNoticeHandler(-1)
    },
    watch: {
        time: {
            deep: true,
            handler(time) {
                if (this.$cart.loading) return
                // tasks#170: the backend moved the selected time and the cart adopted it (Cart.onMovedDeliveryTime): the
                // cart already holds the answer for exactly this time, a second calculate would only repeat it
                const answered = this.$cart.calculation?.required_delivery_time
                if (
                    this.$cart.deliveryTimeAdjustment === 'moved' &&
                    time &&
                    answered &&
                    this.$moment(answered).valueOf() === time
                )
                    return
                this.$cart.calculate()
            },
        },

        calculation: {
            immediate: true,
            async handler(value, valueBefore) {
                // tasks#170: a "moved" notice waits until every pending decision of the alert below is made, so a
                // later run (a calculate started meanwhile) cannot tell it with the toast before the alert opens
                pendingTimeDecisions++
                try {
                    await this.judgeCalculationChange(value, valueBefore)
                } finally {
                    pendingTimeDecisions--
                    if (pendingTimeDecisions === 0) this.tellMovedNotice()
                }
            },
        },
    },
    methods: {
        async judgeCalculationChange(value, valueBefore) {
            // what this calculate did with the selected time; read before any await, a later calculate overwrites it
            const deliveryTimeAdjusted = !!this.$cart.deliveryTimeAdjustment
            if (
                !isEmpty(value) &&
                (value?.restaurant !== valueBefore?.restaurant ||
                    value?.price_sum !== valueBefore?.price_sum ||
                    value?.items?.length !== valueBefore?.items?.length)
            ) {
                this.$store.dispatch('recommender/load', {
                    id: this.getId(value.restaurant),
                    price: value.price_sum,
                    meals: value.items.map((item) => this.getId(item.meal)),
                })
            }
            let deliveryTimeInputsChanged = false
            if (valueBefore) {
                // to open information modal about time or fee change
                if (value.restaurant === valueBefore.restaurant) {
                    // get delivery period if delivery area changes, or if the basket switched the
                    // pre-order rule (task #166): the periods' ASAP availability depends on it
                    if (
                        value.delivery_address_details?.restaurant_area_id !==
                            valueBefore.delivery_address_details?.restaurant_area_id ||
                        value.delivery_address_details?.restaurant_area_is_pre_order !==
                            valueBefore.delivery_address_details?.restaurant_area_is_pre_order
                    ) {
                        try {
                            await this.$store.dispatch('getDeliveryPeriod', {
                                restaurantId: this.getId(value.restaurant),
                                allowanceId: value?.selected_expense_rule?.id,
                                force: true,
                            })
                        } catch (e) {
                            // the time / fee decision below still runs; unusable periods fall back to
                            // the modal (cartPeriodsUsable)
                        }
                    }
                    if (!this.$cart.isCanteen) {
                        deliveryTimeInputsChanged =
                            !this.deliveryTimeChanged &&
                            (value.delivery_address_details?.min_delivery_time !==
                                valueBefore.delivery_address_details?.min_delivery_time ||
                                value.delivery_address_details?.max_delivery_time !==
                                    valueBefore.delivery_address_details?.max_delivery_time ||
                                value.delivery_address_details?.restaurant_area_is_pre_order !==
                                    valueBefore.delivery_address_details
                                        ?.restaurant_area_is_pre_order ||
                                value.delivery_address_details?.restaurant_area_id !==
                                    valueBefore.delivery_address_details?.restaurant_area_id)
                        if (
                            !this.deliveryFeeChanged &&
                            value.price_delivery !== valueBefore.price_delivery
                        ) {
                            this.$store.dispatch('active/setDeliveryFeeChange', true)
                        }
                    }
                }
            }
            let periodRefresh = null
            // task #155: a tiered restaurant's slots depend on the subtotal; untiered periods never match (U1)
            if (!isEmpty(value) && this.$store.getters.deliveryPeriodTierOutdated) {
                periodRefresh = this.$store
                    .dispatch('getDeliveryPeriod', {
                        restaurantId: this.$store.state.deliveryPeriod.committedRestaurantId,
                        // a subtotal refresh changes only the subtotal: keep the committed allowance
                        allowanceId:
                            this.$store.state.deliveryPeriod.committedRestaurantId != null
                                ? this.$store.state.deliveryPeriod.committedAllowanceId
                                : value?.selected_expense_rule?.id,
                    })
                    // a failure is kept in the store (tierTimeState), nothing to do here
                    .catch(() => {})
            }
            // the delivery time changed: tell the customer only if the selected option is gone (tasks#170 F2);
            // a still available ASAP just shows its new range in the cart
            if (deliveryTimeInputsChanged) {
                // availability is judged on the periods of this basket
                if (periodRefresh) await periodRefresh
                if (deliveryTimeAdjusted || this.selectedDeliveryTimeUnavailable(value))
                    this.$store.dispatch('active/setDeliveryTimeChange', true)
            }
        },
        // tasks#170: one notice per change. A time the backend moved (Cart.onMovedDeliveryTime) is told by the
        // "delivery time changed" alert when it is open on this page, since it names the adopted time; otherwise (no
        // time input changed: the time passed on checkout, the place-order calculate; or no alert mounted here, e.g.
        // the mobile toggle cart on a list page) by the #168 toast. Claimed once, so a second mounted cart watcher
        // tells nothing
        tellMovedNotice() {
            const notice = this.$cart.takeMovedNotice()
            if (!notice) return
            const alertRaised = this.$store.getters['active/deliveryTimeChanged']
            if (alertRaised && this.$cart.deliveryTimeAlertMounted) return
            this.$cart.showMovedNotice(notice)
            // the toast told this change where no alert can: an alert mounted later (checkout) must not repeat it
            if (alertRaised) this.$store.dispatch('active/setDeliveryTimeChange', false)
        },
        // the store's periods were fetched for the cart's restaurant and the last fetch gave a usable answer
        // (a failed fetch, or the API client's undefined on a network error, leaves no fresh periods)
        cartPeriodsUsable() {
            return periodsUsableForCart(
                this.$store.state.deliveryPeriod,
                this.getId(this.$cart.calculation?.restaurant)
            )
        },
        // the selected option is gone for this basket: ASAP while pre-order only or without immediate delivery,
        // or a scheduled time that is no longer an offered window. Without usable periods of the cart's restaurant
        // the availability cannot be judged: keep the pre-fix behaviour (every time-input change opens the modal)
        selectedDeliveryTimeUnavailable(value) {
            if (!this.cartPeriodsUsable()) return true
            const deliveryPeriod = this.$store.getters.deliveryPeriod
            if (this.$store.getters['order/asap'])
                return (
                    !!value.delivery_address_details?.restaurant_area_is_pre_order ||
                    !deliveryPeriod?.immediate_available
                )
            return !isOfferedSlotStart(deliveryPeriod, this.$store.getters['order/time'])
        },
    },
}
